import { useLocation } from 'react-router-dom';
import { useOrderContext } from '../../../../../contexts/Order.context';
import { useOrderCalculateContext } from '../../../../../contexts/OrderCalculate.context';
import { useOrderProductsContext } from '../../../../../contexts/OrderProducts.context';
import { useOrderProductsStickyParentGroupSlot } from '../../../../../contexts/OrderProductsStickyParentGroupSlot.context';
import {
  OrderHeadersGroupByEnum,
  OrderProductsViewActionsEnum,
} from '../../../../orderProducts.types';
import {
  ParentGroupHeaderPlacement,
  ParentGroupSections,
} from '../../../orderProductsSection.types';
import {
  getGroupBySearchParam,
  getParentGroupSections,
  getSectionSelection,
  getSectionTotals,
  resolveParentGroupHeaderPlacement,
  sumSectionsTotals,
} from '../../../orderProductsSection.utils';
import { OrderProductSectionHeaderProps } from '../orderProductSectionHeader.types';

type UseOrderProductSectionHeaderParams = Pick<
  OrderProductSectionHeaderProps,
  'sections' | 'sectionIndex' | 'selectedItems'
>;

const EMPTY_PARENT_GROUP: ParentGroupSections = { sections: [], isExpanded: false };

const useOrderProductSectionHeader = ({
  sections,
  sectionIndex,
  selectedItems,
}: UseOrderProductSectionHeaderParams) => {
  const location = useLocation();
  const {
    productsByKitId,
    collapsedParentGroupKeys,
    updateGroupProductsCount,
    updateParentGroupExpansion,
  } = useOrderProductsContext();
  const { summaryTotals } = useOrderCalculateContext();
  const { changeOrderReviewAction } = useOrderContext();
  const stickyParentGroupSlot = useOrderProductsStickyParentGroupSlot();

  const groupBy = getGroupBySearchParam(location.search);
  const section = sections[sectionIndex];
  const isGroupSection = groupBy !== OrderHeadersGroupByEnum.DEFAULT && sectionIndex > 0;
  const isSectionExpanded = section?.isExpanded ?? false;

  const totals = getSectionTotals({ section, groupBy, headers: summaryTotals?.headers });
  const selection = getSectionSelection({
    selectedItems,
    productsByKitId,
    sectionProducts: section?.products ?? [],
  });

  const parentGroupKey = section?.parentGroupKey;

  const { sections: parentGroupSections, isExpanded: isParentGroupExpanded } = parentGroupKey
    ? getParentGroupSections({ sections, parentGroupKey, collapsedParentGroupKeys })
    : EMPTY_PARENT_GROUP;

  const isSubGroupVisible = !parentGroupKey || isParentGroupExpanded;

  const bulkActions = {
    onCollapseAll: () => changeOrderReviewAction(OrderProductsViewActionsEnum.HIDE_ALL),
    onExpandAll: () => changeOrderReviewAction(OrderProductsViewActionsEnum.EXPAND_ALL),
  };

  const parentGroupPlacement = resolveParentGroupHeaderPlacement({
    section,
    parentGroupSections,
    hasStickyParentGroupSlot: !!stickyParentGroupSlot,
    isParentGroupExpanded,
  });

  const parentGroupContainer =
    parentGroupPlacement === ParentGroupHeaderPlacement.STICKY_SLOT ? stickyParentGroupSlot : null;

  const parentGroup =
    parentGroupPlacement !== ParentGroupHeaderPlacement.NONE && parentGroupKey
      ? {
          container: parentGroupContainer,
          totals: sumSectionsTotals(
            parentGroupSections.map((parentGroupSection) =>
              getSectionTotals({
                section: parentGroupSection,
                groupBy,
                headers: summaryTotals?.headers,
              }),
            ),
          ),
          selection: getSectionSelection({
            selectedItems,
            productsByKitId,
            sectionProducts: parentGroupSections.flatMap(
              (parentGroupSection) => parentGroupSection.products,
            ),
          }),
          isExpanded: isParentGroupExpanded,
          bulkToggle: {
            ...bulkActions,
            isExpanded: parentGroupSections.some(
              (parentGroupSection) => parentGroupSection.isExpanded,
            ),
          },
          toggleExpanded: () => updateParentGroupExpansion(parentGroupKey, !isParentGroupExpanded),
        }
      : undefined;

  return {
    section,
    totals,
    selection,
    parentGroup,
    isGroupSection,
    isSectionExpanded,
    isSubGroupVisible,
    isParentGroupInline: parentGroupPlacement === ParentGroupHeaderPlacement.INLINE,
    bulkToggle: { ...bulkActions, isExpanded: isSectionExpanded },
    toggleGroup: () => updateGroupProductsCount(sectionIndex),
  };
};

export default useOrderProductSectionHeader;
